/**
 * Faturar a condicional — ela vira uma venda de verdade via `POST /vendas`
 * com `condicional` preenchido (ver `plataforma/vendas.ts`,
 * `faturarCondicional`).
 *
 * ROTA PRÓPRIA, e não gaveta aberta a partir da listagem — decisão tomada
 * aqui sem instrução explícita, registrada em `docs/de-para.md`:
 *
 * `Fechamento` (o MESMO componente do PDV, reaproveitado sem cópia) já abre
 * a PRÓPRIA gaveta para dividir o pagamento em duas formas
 * (`pdv/Fechamento.tsx`, "A segunda forma vive numa GAVETA"). Encaixar esse
 * componente dentro de OUTRA gaveta (aberta a partir da listagem) empilharia
 * dois diálogos modais com foco preso ao mesmo tempo, cada um escutando Esc
 * por conta própria (`componentes/foco.ts`): fechar a gaveta de dentro com
 * Esc dispararia os DOIS `aoFechar` no mesmo evento, porque os dois ouvintes
 * estão no `document`. Uma rota resolve isso de graça: só existe UM modal na
 * tela por vez — o de dividir pagamento —, exatamente como no PDV, onde o
 * carrinho não é modal nenhum.
 *
 * A ficha da condicional só traz `{ codigo, nome }` do cliente — o limite e o
 * `parcelas_max` que o `Fechamento` precisa para o crediário vêm de
 * `GET /clientes/{codigo}`, a MESMA fonte que o PDV usa para a busca de
 * cliente.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DialogoPIN } from '../../componentes/DialogoPIN'
import { Icone } from '../../componentes/Icone'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import { carregarCondicional, type Condicional } from '../../plataforma/condicional'
import { carregarFicha, type FichaCliente } from '../../plataforma/clientes'
import { useSessao } from '../../plataforma/contexto-sessao'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { faturarCondicional } from '../../plataforma/vendas'
import { LayoutApp } from '../app/LayoutApp'
import { Fechamento, type DadosFechamento } from '../pdv/Fechamento'

type ErroDoFaturamento = {
  campos: Record<string, string>
  mensagem: string
}

export function TelaFaturarCondicional() {
  const { codigo = '' } = useParams()
  const navegar = useNavigate()
  const { pode } = useSessao()
  const podeFaturar = pode('sale.create')

  const [condicional, setCondicional] = useState<Condicional | null>(null)
  const [cliente, setCliente] = useState<FichaCliente | null>(null)
  const [erroCarregar, setErroCarregar] = useState('')

  const [enviando, setEnviando] = useState(false)
  const [erroVenda, setErroVenda] = useState<ErroDoFaturamento | null>(null)
  const [pinPara, setPinPara] = useState('')
  const [erroPIN, setErroPIN] = useState('')
  // Guarda o que a pessoa montou, para o reenvio com PIN sair IGUAL — mesmo
  // desenho de `pdv/TelaPDV.tsx`.
  const ultimoFechamento = useRef<DadosFechamento | null>(null)

  useEffect(() => {
    const controle = new AbortController()
    let cancelado = false

    carregarCondicional(codigo, controle.signal)
      .then(async (dados) => {
        if (cancelado) return
        setCondicional(dados)
        if (dados.status !== 'aberta') return
        const ficha = await carregarFicha(dados.cliente.codigo, controle.signal)
        if (!cancelado) setCliente(ficha)
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroCarregar(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a condicional.',
        )
      })

    return () => {
      cancelado = true
      controle.abort()
    }
  }, [codigo])

  const totalItens = condicional?.itens.reduce((soma, item) => soma + item.quantidade, 0) ?? 0

  const finalizar = useCallback(
    async (dados: DadosFechamento, pin?: string) => {
      if (!condicional) return
      ultimoFechamento.current = dados
      setEnviando(true)
      setErroVenda(null)
      try {
        const venda = await faturarCondicional(
          condicional.codigo,
          { descontoCentavos: dados.descontoCentavos, pagamentos: dados.pagamentos },
          pin,
        )
        setPinPara('')
        setErroPIN('')
        mostrarAviso(`Condicional ${condicional.codigo} faturada — venda ${venda.codigo}.`)
        navegar('/app/condicionais')
      } catch (causa: unknown) {
        if (!(causa instanceof ErroApi)) {
          setErroVenda({ campos: {}, mensagem: 'Não foi possível faturar a condicional.' })
          return
        }
        if (causa.pinNecessario) {
          setPinPara(causa.pinNecessario)
          setErroPIN('')
          return
        }
        if (causa.codigo === 'pin_invalido') {
          setErroPIN(causa.message)
          return
        }
        setPinPara('')
        setErroVenda({ campos: causa.campos, mensagem: causa.message })
      } finally {
        setEnviando(false)
      }
    },
    [condicional, navegar],
  )

  const camposDoErro = erroVenda?.campos ?? {}
  const erroDesconto = camposDoErro.desconto_centavos ? (erroVenda?.mensagem ?? '') : ''
  const erroPagamento =
    camposDoErro.pagamentos || camposDoErro.pagamento_1 || camposDoErro.pagamento_2 || camposDoErro.cliente
      ? (erroVenda?.mensagem ?? '')
      : ''
  const erroGeral =
    erroCarregar || (erroVenda && !erroDesconto && !erroPagamento ? erroVenda.mensagem : '')

  const condicionalEncerrada = condicional && condicional.status !== 'aberta'

  return (
    <LayoutApp
      titulo={condicional ? `Faturar ${condicional.codigo}` : 'Faturar condicional'}
      subtitulo={condicional ? condicional.cliente.nome : 'Carregando…'}
      trilha={[{ rotulo: 'Condicional', para: '/app/condicionais' }, { rotulo: codigo }]}
      voltarPara="/app/condicionais"
      largura="formulario"
    >
      {erroGeral && (
        <div role="alert" className="mb-4 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
          {erroGeral}
        </div>
      )}

      {!condicional ? (
        <div className="h-64 animate-pulse rounded-sistema bg-superficie" />
      ) : condicionalEncerrada ? (
        <div className="rounded-sistema border border-borda bg-superficie px-5 py-8 text-center">
          <Icone nome="warning" tamanho={26} className="mx-auto text-inativo" />
          <p className="mt-2.5 text-sm font-semibold text-tinta">
            Esta condicional está {condicional.status_rotulo.toLowerCase()}
          </p>
          <p className="mt-1 text-[13px] text-texto-suave">
            Só uma condicional aberta pode ser faturada.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-sistema border border-borda bg-superficie">
            {condicional.itens.map((item, indice) => (
              <div
                key={item.numero_item}
                className={`flex items-center gap-3 px-4 py-3 ${indice ? 'border-t border-divisoria' : ''}`}
              >
                <Icone nome="apparel" tamanho={18} className="shrink-0 text-texto-fraco" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold text-tinta">{item.sku.produto}</p>
                  <p className="tabular text-xs text-texto-suave">
                    {item.sku.cor} · {item.sku.tamanho} · {item.quantidade}×{' '}
                    {formatarMoeda(item.preco_unitario_centavos)}
                  </p>
                </div>
                <span className="tabular shrink-0 text-[13.5px] font-bold text-tinta">
                  {formatarMoeda(item.total_centavos)}
                </span>
              </div>
            ))}
          </div>

          <div className="rounded-sistema border border-borda bg-superficie">
            {!cliente ? (
              <div className="h-40 animate-pulse rounded-sistema bg-superficie-suave" />
            ) : (
              <Fechamento
                totalItens={totalItens}
                subtotalCentavos={condicional.subtotal_centavos}
                cliente={cliente}
                podeVender={podeFaturar}
                enviando={enviando}
                erroDesconto={erroDesconto}
                erroPagamento={erroPagamento}
                aoFinalizar={(dados) => void finalizar(dados)}
                aoAlterar={() => setErroVenda(null)}
                rotuloAcao="Faturar condicional"
                rotuloAcaoOcupado="Faturando…"
              />
            )}
          </div>
        </div>
      )}

      {pinPara && (
        <DialogoPIN
          permissao={pinPara}
          erro={erroPIN}
          ocupado={enviando}
          aoConfirmar={(pin) => {
            const pendente = ultimoFechamento.current
            if (pendente) void finalizar(pendente, pin)
          }}
          aoCancelar={() => {
            setPinPara('')
            setErroPIN('')
          }}
        />
      )}
    </LayoutApp>
  )
}
